import React, { useState, useEffect, useCallback } from 'react';
import type { RacingRulesVersion, ExtractionStatus, RacingRuleSection } from '../../types';
import { racingRulesService } from '../../services/racingRulesService';

/**
 * RacingRulesManager — Admin component for uploading and managing
 * Racing Rules of Sailing PDFs and viewing extraction results.
 *
 * Follows the SpecialRegulationsManager pattern with part-based grouping
 * instead of requirementType grouping.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 */

interface SectionGroup {
  part: string;
  sections: RacingRuleSection[];
}

function groupByPart(sections: RacingRuleSection[]): SectionGroup[] {
  const groups: Record<string, RacingRuleSection[]> = {};
  for (const section of sections) {
    if (!groups[section.part]) {
      groups[section.part] = [];
    }
    groups[section.part].push(section);
  }

  return Object.entries(groups)
    .map(([part, secs]) => ({
      part,
      sections: secs.sort((a, b) => a.ruleNumber.localeCompare(b.ruleNumber, undefined, { numeric: true })),
    }))
    .sort((a, b) => a.part.localeCompare(b.part));
}

const RacingRulesManager: React.FC = () => {
  const [version, setVersion] = useState<RacingRulesVersion | null>(null);
  const [versionLoading, setVersionLoading] = useState(true);
  const [versionError, setVersionError] = useState<string | null>(null);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [showExtractionStatus, setShowExtractionStatus] = useState(false);

  const [sections, setSections] = useState<RacingRuleSection[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const toggleGroup = (part: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(part)) {
        next.delete(part);
      } else {
        next.add(part);
      }
      return next;
    });
  };

  const handleDelete = async (versionId: string) => {
    if (!window.confirm(`Delete Racing Rules version ${versionId}? This will remove all sections and cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await racingRulesService.deleteVersion(versionId);
      setVersion(null);
      setSections([]);
    } catch {
      setDeleteError('Failed to delete version.');
    } finally {
      setDeleting(false);
    }
  };

  const fetchVersion = useCallback(async () => {
    setVersionLoading(true);
    setVersionError(null);
    try {
      const data = await racingRulesService.getVersionInfo();
      setVersion(data);
    } catch {
      setVersionError('Failed to load version information.');
    } finally {
      setVersionLoading(false);
    }
  }, []);

  const fetchSections = useCallback(async () => {
    setSectionsLoading(true);
    setSectionsError(null);
    try {
      const data = await racingRulesService.listSections();
      setSections(data);
    } catch {
      setSectionsError('Failed to load racing rules sections.');
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  const fetchExtractionStatus = useCallback(async () => {
    try {
      const status = await racingRulesService.getExtractionStatus();
      setExtractionStatus(status);
      return status;
    } catch {
      setExtractionStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchVersion();
    // Check extraction status on load - only show if still processing
    fetchExtractionStatus().then((status) => {
      if (status && status.status === 'processing') {
        setShowExtractionStatus(true);
        setPolling(true);
      }
    });
  }, [fetchVersion, fetchExtractionStatus]);

  useEffect(() => {
    if (version) {
      fetchSections();
    }
  }, [version, fetchSections]);

  useEffect(() => {
    if (!polling) return;

    const intervalId = setInterval(async () => {
      const status = await fetchExtractionStatus();
      if (status && status.status !== 'processing') {
        setPolling(false);
        if (status.status === 'complete') {
          fetchVersion();
        }
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [polling, fetchExtractionStatus, fetchVersion]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !effectiveDate) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const result = await racingRulesService.uploadRacingRules(uploadFile, effectiveDate);
      setUploadSuccess(`Upload started. Version: ${result.versionId}`);
      setExtractionStatus({ status: 'processing', versionId: result.versionId });
      setShowExtractionStatus(true);
      setPolling(true);
      setUploadFile(null);
      setEffectiveDate('');
      const fileInput = document.getElementById('racing-rules-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch {
      setUploadError('Failed to upload Racing Rules PDF.');
    } finally {
      setUploading(false);
    }
  };

  const sectionGroups = groupByPart(sections);

  return (
    <div className="racing-rules-manager" data-testid="racing-rules-manager">
      <h2>Racing Rules of Sailing</h2>

      {/* Current Version Display */}
      <div data-testid="version-section">
        <h3>Current Version</h3>
        {versionLoading && <p data-testid="version-loading">Loading version information…</p>}
        {versionError && (
          <div className="error-message" data-testid="version-error">
            {versionError}
          </div>
        )}
        {!versionLoading && !versionError && !version && (
          <div className="info-message" data-testid="empty-state">
            No Racing Rules have been uploaded yet. Use the form below to upload a
            Racing Rules of Sailing PDF.
          </div>
        )}
        {!versionLoading && !versionError && version && (
          <table className="global-documents-table" data-testid="version-table">
            <thead>
              <tr>
                <th>Version ID</th>
                <th>Effective Date</th>
                <th>Section Count</th>
                <th>Uploaded At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="version-row">
                <td data-testid="version-id">{version.versionId}</td>
                <td data-testid="version-effective-date">{version.effectiveDate}</td>
                <td data-testid="version-section-count">{version.sectionCount}</td>
                <td data-testid="version-uploaded-at">
                  {new Date(version.uploadedAt).toLocaleString()}
                </td>
                <td>
                  <button
                    onClick={() => handleDelete(version.versionId)}
                    disabled={deleting}
                    title="Delete this version"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    data-testid="delete-version-btn"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {deleteError && (
          <div className="error-message" data-testid="delete-error">
            {deleteError}
          </div>
        )}
      </div>

      {/* Extraction Status Indicator - only shown during/after upload in current session */}
      {showExtractionStatus && extractionStatus && extractionStatus.status === 'processing' && (
        <div className="info-message" data-testid="extraction-processing">
          <strong>Extraction in progress…</strong> Version: {extractionStatus.versionId}.
          {extractionStatus.progress && (
            <div data-testid="extraction-progress" style={{ marginTop: '8px' }}>
              {extractionStatus.progress}
            </div>
          )}
          <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#666' }}>
            Status will update automatically.
          </div>
        </div>
      )}
      {showExtractionStatus && extractionStatus && extractionStatus.status === 'complete' && (
        <div className="success-message" data-testid="extraction-complete">
          Extraction complete. {extractionStatus.sectionCount} sections extracted
          for version {extractionStatus.versionId}.
        </div>
      )}
      {showExtractionStatus && extractionStatus && extractionStatus.status === 'failed' && (
        <div className="error-message" data-testid="extraction-failed">
          Extraction failed for version {extractionStatus.versionId}.
          {extractionStatus.error && ` Error: ${extractionStatus.error}`}
        </div>
      )}

      {/* Upload Form */}
      <form onSubmit={handleUpload} data-testid="upload-form">
        <h3>Upload Racing Rules PDF</h3>

        {uploadSuccess && (
          <div className="success-message" data-testid="upload-success">
            {uploadSuccess}
          </div>
        )}
        {uploadError && (
          <div className="error-message" data-testid="upload-error">
            {uploadError}
          </div>
        )}

        <div className="form-field">
          <label htmlFor="racing-rules-file">PDF File:</label>
          <input
            id="racing-rules-file"
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            data-testid="input-file"
          />
        </div>

        <div className="form-field">
          <label htmlFor="racing-rules-effective-date">Effective Date:</label>
          <input
            id="racing-rules-effective-date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            data-testid="input-effective-date"
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={uploading || !uploadFile || !effectiveDate}
            data-testid="upload-submit-btn"
          >
            {uploading ? 'Uploading…' : 'Upload & Extract'}
          </button>
        </div>
      </form>

      {/* Extraction Results Summary */}
      {!versionLoading && version && (
        <div data-testid="results-section">
          <h3>Extraction Results</h3>

          {sectionsLoading && <p data-testid="sections-loading">Loading sections…</p>}
          {sectionsError && (
            <div className="error-message" data-testid="sections-error">
              {sectionsError}
            </div>
          )}

          {!sectionsLoading && !sectionsError && sections.length === 0 && (
            <p data-testid="no-sections">No sections found for the current version.</p>
          )}

          {!sectionsLoading && !sectionsError && sectionGroups.length > 0 && (
            <div data-testid="section-groups">
              <p data-testid="total-section-count">
                Total: {sections.length} section{sections.length !== 1 ? 's' : ''} across {sectionGroups.length} group{sectionGroups.length !== 1 ? 's' : ''}
              </p>
              {sectionGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.part);
                return (
                  <div
                    key={group.part}
                    className="category-section"
                    data-testid={`group-${group.part}`}
                  >
                    <div
                      className="category-header"
                      onClick={() => toggleGroup(group.part)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ marginRight: '8px' }}>{isExpanded ? '▼' : '▶'}</span>
                      <h4 style={{ display: 'inline' }}>{group.part}</h4>
                      <span className="requirement-count">
                        ({group.sections.length} section{group.sections.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="category-details">
                        <table className="requirements-table" data-testid={`table-${group.part}`}>
                          <thead>
                            <tr>
                              <th>Rule Number</th>
                              <th>Title</th>
                              <th>Type</th>
                              <th>Page</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.sections.map((section) => (
                              <tr key={section.ruleNumber} data-testid={`section-row-${section.ruleNumber}`}>
                                <td>{section.ruleNumber}</td>
                                <td>{section.title}</td>
                                <td>{section.ruleType}</td>
                                <td>{section.pageNumber}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RacingRulesManager;
